import { useState } from "react";
import type { MissionIoBlock, ParsedMissionStep } from "../../utils/missionTrace";
import {
  aggregateByModel,
  buildMissionStepTree,
  formatDateTime,
  formatModelDisplay,
  formatMs,
  groupParallelDelegateRoots,
  KIND_LABELS,
  resolveStepAgents,
  resolveStepModels,
  resolvedModelId,
  stepEndIso,
  timelineSteps,
  type MissionOrigin,
} from "../../utils/missionTrace";
import { formatCostMillicents, formatUsdMicros } from "@vida/currents-core";
import { MissionTimeline } from "./MissionTimeline";
import { MissionOriginBanner, type ResolvedUser } from "./MissionOriginBanner";

function formatTokens(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT");
}

function StepCostBadges({ step }: { step: ParsedMissionStep }) {
  const hasMicros =
    step.providerUsdMicros != null ||
    step.chargedUsdMicros != null ||
    step.costMillicents != null;
  if (!hasMicros) return null;

  const charged =
    step.chargedUsdMicros != null
      ? formatUsdMicros(step.chargedUsdMicros)
      : formatCostMillicents(step.costMillicents);

  return (
    <div className="trace-step-costs">
      {step.providerUsdMicros != null && (
        <span className="chip" title="Costo cloud AWS (provider)">
          ☁️ {formatUsdMicros(step.providerUsdMicros)}
        </span>
      )}
      <span className="chip" title="Costo addebitato (× markup)">
        💳 {charged}
      </span>
      {step.markup != null && (
        <span className="chip chip--muted" title="Markup applicato">
          ×{step.markup}
        </span>
      )}
    </div>
  );
}

function StepMetrics({ step }: { step: ParsedMissionStep }) {
  const hasMetrics =
    step.durationMs != null ||
    step.costMillicents != null ||
    step.chargedUsdMicros != null ||
    step.inputTokens != null;
  if (!hasMetrics) return null;
  return (
    <div className="trace-step-metrics">
      {step.durationMs != null && <span className="chip">⏱ {formatMs(step.durationMs)}</span>}
      <StepCostBadges step={step} />
      {(step.inputTokens != null || step.outputTokens != null) && (
        <span className="chip">
          🔤 {formatTokens(step.inputTokens)} → {formatTokens(step.outputTokens)}
        </span>
      )}
    </div>
  );
}

function IoSection({
  title,
  blocks,
  emptyLabel,
}: {
  title: string;
  blocks: MissionIoBlock[];
  emptyLabel?: string;
}) {
  if (blocks.length === 0) {
    if (!emptyLabel) return null;
    return (
      <div className="mission-io-section">
        <div className="mission-io-title">{title}</div>
        <p className="muted mission-io-empty">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="mission-io-section">
      <div className="mission-io-title">{title}</div>
      {blocks.map((b) => (
        <div key={`${title}-${b.label}`} className="mission-io-block">
          <div className="mission-io-label">{b.label}</div>
          <pre className="mission-io-value">{b.value}</pre>
        </div>
      ))}
    </div>
  );
}

const MODEL_STEP_KINDS = new Set(["llm_turn", "model", "model_start"]);

function StepCard({
  step,
  depth = 0,
  missionT0,
  stepModels,
}: {
  step: ParsedMissionStep;
  depth?: number;
  missionT0?: number | null;
  stepModels?: Map<number, string>;
}) {
  const isDelegate = step.kind === "delegate";
  const isParallelGroup = step.kind === "delegate_parallel";
  const hasChildren = (step.children?.length ?? 0) > 0;
  const modelId = resolvedModelId(step, stepModels);
  const modelShort = modelId ? formatModelDisplay(modelId).short : null;
  const kindLabel = KIND_LABELS[step.kind] ?? step.kind;
  const showSummary =
    Boolean(step.summary) && !(MODEL_STEP_KINDS.has(step.kind) && modelId);
  const offsetMs =
    missionT0 != null && step.startedAt
      ? new Date(step.startedAt).getTime() - missionT0
      : missionT0 != null
        ? new Date(step.createdAt).getTime() - missionT0
        : null;

  return (
    <div
      className={`trace-step${isParallelGroup ? " trace-step--parallel" : ""}`}
      style={depth > 0 ? { marginLeft: `${depth * 1.25}rem` } : undefined}
    >
      <div className="trace-step-header">
        <strong
          title={
            MODEL_STEP_KINDS.has(step.kind) && modelId
              ? formatModelDisplay(modelId).full
              : step.skillId ?? undefined
          }
        >
          #{step.seq}{" "}
          {MODEL_STEP_KINDS.has(step.kind) && modelShort
            ? `${kindLabel} · ${modelShort}`
            : kindLabel}
        </strong>
        {showSummary && <span className="muted trace-step-summary">{step.summary}</span>}
        {step.startedAt && (
          <time className="muted trace-step-time" dateTime={step.startedAt} title="Avvio step">
            ▶ {formatDateTime(step.startedAt)}
          </time>
        )}
        {offsetMs != null && offsetMs >= 0 && (
          <span className="muted trace-step-offset">+{formatMs(offsetMs)}</span>
        )}
        {stepEndIso(step) && (
          <time className="muted trace-step-time" dateTime={stepEndIso(step)!} title="Fine step">
            ■ {formatDateTime(stepEndIso(step))}
          </time>
        )}
      </div>
      <StepMetrics step={step} />
      <IoSection title="Input" blocks={step.input} emptyLabel="—" />
      <IoSection title="Output" blocks={step.output} emptyLabel="—" />
      {isParallelGroup && hasChildren && (
        <div className="trace-parallel-group">
          {step.children!.map((child) => (
            <StepCard key={child.seq} step={child} depth={depth + 1} missionT0={missionT0} stepModels={stepModels} />
          ))}
        </div>
      )}
      {isDelegate && hasChildren && (
        <details className="trace-delegate-children" open>
          <summary className="muted">Sub-step del peer ({step.children!.length})</summary>
          {step.children!.map((child) => (
            <StepCard key={child.seq} step={child} depth={depth + 1} missionT0={missionT0} stepModels={stepModels} />
          ))}
        </details>
      )}
      {!isDelegate && !isParallelGroup && hasChildren && (
        <div className="trace-step-children">
          {step.children!.map((child) => (
            <StepCard key={child.seq} step={child} depth={depth + 1} missionT0={missionT0} stepModels={stepModels} />
          ))}
        </div>
      )}
    </div>
  );
}

function missionTotals(steps: ParsedMissionStep[]) {
  let durationMs = 0;
  let providerUsdMicros = 0;
  let chargedUsdMicros = 0;
  let costMillicents = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  for (const s of steps) {
    if (s.durationMs != null) durationMs += s.durationMs;
    if (s.providerUsdMicros != null) providerUsdMicros += s.providerUsdMicros;
    if (s.chargedUsdMicros != null) chargedUsdMicros += s.chargedUsdMicros;
    if (s.costMillicents != null) costMillicents += s.costMillicents;
    if (s.inputTokens != null) inputTokens += s.inputTokens;
    if (s.outputTokens != null) outputTokens += s.outputTokens;
  }
  return { durationMs, providerUsdMicros, chargedUsdMicros, costMillicents, inputTokens, outputTokens };
}

function ModelCostTable({
  rows,
  pricingUnmatched,
}: {
  rows: ReturnType<typeof aggregateByModel>;
  pricingUnmatched?: boolean | null;
}) {
  if (rows.length === 0) return null;
  const totals = rows.reduce(
    (acc, r) => ({
      stepCount: acc.stepCount + r.stepCount,
      providerUsdMicros: acc.providerUsdMicros + r.providerUsdMicros,
      chargedUsdMicros: acc.chargedUsdMicros + r.chargedUsdMicros,
      inputTokens: acc.inputTokens + r.inputTokens,
      outputTokens: acc.outputTokens + r.outputTokens,
    }),
    { stepCount: 0, providerUsdMicros: 0, chargedUsdMicros: 0, inputTokens: 0, outputTokens: 0 },
  );

  return (
    <section className="mission-model-costs">
      <h3 className="mission-trace-subtitle">Costi per modello</h3>
      <div className="insights-table-wrap">
        <table className="insights-table mission-model-costs-table">
          <thead>
            <tr>
              <th>Modello</th>
              <th># step</th>
              <th>Cloud</th>
              <th>Addebitato</th>
              <th>Token in → out</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.modelId}>
                <td>
                  <code title={formatModelDisplay(row.modelId).full}>
                    {formatModelDisplay(row.modelId).short}
                  </code>
                  {(row.pricingUnmatched || pricingUnmatched) &&
                    row.providerUsdMicros === 0 &&
                    row.chargedUsdMicros === 0 && (
                      <span className="chip chip--warn" title="Costo non stimato">
                        ⚠️
                      </span>
                    )}
                </td>
                <td>{row.stepCount}</td>
                <td>{row.providerUsdMicros > 0 ? formatUsdMicros(row.providerUsdMicros) : "—"}</td>
                <td>{row.chargedUsdMicros > 0 ? formatUsdMicros(row.chargedUsdMicros) : "—"}</td>
                <td>
                  {formatTokens(row.inputTokens)} → {formatTokens(row.outputTokens)}
                </td>
              </tr>
            ))}
            <tr className="mission-model-costs-total">
              <td>
                <strong>Totale</strong>
              </td>
              <td>{totals.stepCount}</td>
              <td>{totals.providerUsdMicros > 0 ? formatUsdMicros(totals.providerUsdMicros) : "—"}</td>
              <td>{totals.chargedUsdMicros > 0 ? formatUsdMicros(totals.chargedUsdMicros) : "—"}</td>
              <td>
                {formatTokens(totals.inputTokens)} → {formatTokens(totals.outputTokens)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StepDetailMeta({
  step,
  agentName,
  stepModels,
}: {
  step: ParsedMissionStep;
  agentName?: string | null;
  stepModels?: Map<number, string>;
}) {
  const modelId = resolvedModelId(step, stepModels);
  const modelShort =
    modelId && (step.kind === "llm_turn" || step.kind === "model" || step.kind === "model_start")
      ? formatModelDisplay(modelId).short
      : null;

  return (
    <dl className="mission-step-detail-meta">
      {agentName && (
        <div className="mission-step-detail-meta-row">
          <dt>Agente</dt>
          <dd>{agentName}</dd>
        </div>
      )}
      {modelShort && modelId && (
        <div className="mission-step-detail-meta-row">
          <dt>Modello</dt>
          <dd>
            <span title={formatModelDisplay(modelId).full}>{modelShort}</span>
          </dd>
        </div>
      )}
      {step.startedAt && (
        <div className="mission-step-detail-meta-row">
          <dt>Avvio</dt>
          <dd>
            <time dateTime={step.startedAt}>{formatDateTime(step.startedAt)}</time>
          </dd>
        </div>
      )}
      {stepEndIso(step) && (
        <div className="mission-step-detail-meta-row">
          <dt>Fine</dt>
          <dd>
            <time dateTime={stepEndIso(step)!}>{formatDateTime(stepEndIso(step))}</time>
          </dd>
        </div>
      )}
      {step.durationMs != null && (
        <div className="mission-step-detail-meta-row">
          <dt>Durata</dt>
          <dd>{formatMs(step.durationMs)}</dd>
        </div>
      )}
      {(step.providerUsdMicros != null || step.chargedUsdMicros != null) && (
        <div className="mission-step-detail-meta-row">
          <dt>Costo</dt>
          <dd>
            {step.providerUsdMicros != null && (
              <span title="Costo cloud AWS (provider)">
                ☁️ {formatUsdMicros(step.providerUsdMicros)}
              </span>
            )}
            {step.chargedUsdMicros != null && (
              <span title="Costo addebitato (× markup)">
                {" "}
                💳 {formatUsdMicros(step.chargedUsdMicros)}
              </span>
            )}
          </dd>
        </div>
      )}
      {(step.inputTokens != null || step.outputTokens != null) && (
        <div className="mission-step-detail-meta-row">
          <dt>Token</dt>
          <dd>
            {formatTokens(step.inputTokens)} → {formatTokens(step.outputTokens)}
          </dd>
        </div>
      )}
      {step.exitCode != null && (
        <div className="mission-step-detail-meta-row">
          <dt>Exit code</dt>
          <dd>{step.exitCode}</dd>
        </div>
      )}
      {step.errorCategory && (
        <div className="mission-step-detail-meta-row">
          <dt>Categoria errore</dt>
          <dd>{step.errorCategory}</dd>
        </div>
      )}
    </dl>
  );
}

type TraceView = "list" | "timeline";

export function MissionTracePanel({
  missionInput,
  missionOutput,
  steps,
  pricingUnmatched,
  missionMarkup,
  missionAgentName,
  missionOrigin,
  resolvedUser,
  defaultView = "list",
}: {
  missionInput: MissionIoBlock[];
  missionOutput: MissionIoBlock[];
  steps: ParsedMissionStep[];
  pricingUnmatched?: boolean | null;
  missionMarkup?: number | null;
  missionAgentName?: string | null;
  missionOrigin?: MissionOrigin | null;
  resolvedUser?: ResolvedUser | null;
  defaultView?: TraceView;
}) {
  const [view, setView] = useState<TraceView>(defaultView);
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const tree = groupParallelDelegateRoots(buildMissionStepTree(steps));
  const flatSteps = timelineSteps(steps);
  const stepAgents = resolveStepAgents(steps, missionAgentName);
  const stepModels = resolveStepModels(steps);
  const modelRows = aggregateByModel(steps);
  const startStep = steps.find((s) => s.kind === "start");
  const missionT0 = startStep ? new Date(startStep.createdAt).getTime() : null;
  const totals = missionTotals(steps);
  const chargedDisplay =
    totals.chargedUsdMicros > 0
      ? formatUsdMicros(totals.chargedUsdMicros)
      : formatCostMillicents(totals.costMillicents);

  return (
    <div className="card mission-trace-panel">
      {missionOrigin && (
        <MissionOriginBanner
          origin={missionOrigin}
          resolvedUser={resolvedUser}
          durationMs={totals.durationMs}
        />
      )}
      <div className="mission-trace-header">
        <h2 style={{ marginTop: 0 }}>Missione</h2>
        {pricingUnmatched && (
          <span className="chip chip--warn" title="Almeno un turno senza rate in tabella">
            ⚠️ Stima pricing
          </span>
        )}
        {steps.length > 0 && (
          <div className="trace-mission-totals">
            <span className="chip">⏱ {formatMs(totals.durationMs)}</span>
            {totals.providerUsdMicros > 0 && (
              <span className="chip" title="Costo cloud">
                ☁️ {formatUsdMicros(totals.providerUsdMicros)}
              </span>
            )}
            <span className="chip" title="Addebitato">
              💳 {chargedDisplay}
            </span>
            {missionMarkup != null && (
              <span className="chip chip--muted">×{missionMarkup}</span>
            )}
            <span className="chip">
              🔤 {formatTokens(totals.inputTokens)} → {formatTokens(totals.outputTokens)}
            </span>
          </div>
        )}
      </div>

      <div className="mission-trace-view-toggle">
        <h3 className="mission-trace-subtitle">Step ({flatSteps.length})</h3>
        <div className="mission-trace-view-buttons" role="tablist" aria-label="Vista step">
          <button
            type="button"
            className={`btn secondary${view === "timeline" ? " active" : ""}`}
            role="tab"
            aria-selected={view === "timeline"}
            onClick={() => setView("timeline")}
          >
            Timeline
          </button>
          <button
            type="button"
            className={`btn secondary${view === "list" ? " active" : ""}`}
            role="tab"
            aria-selected={view === "list"}
            onClick={() => setView("list")}
          >
            Lista
          </button>
        </div>
      </div>
      {view === "list" ? (
        tree.length > 0 ? (
          tree.map((s) => <StepCard key={s.seq} step={s} missionT0={missionT0} stepModels={stepModels} />)
        ) : (
          <p className="muted">Nessuno step da visualizzare.</p>
        )
      ) : (
        <>
          <MissionTimeline
            steps={steps}
            selectedSeq={selectedSeq}
            stepAgents={stepAgents}
            stepModels={stepModels}
            onSelect={(s) => setSelectedSeq((prev) => (prev === s.seq ? null : s.seq))}
          />
          {selectedSeq != null && (() => {
            const selected = flatSteps.find((s) => s.seq === selectedSeq);
            if (!selected) return null;
            const agentName = stepAgents.get(selected.seq);
            return (
              <section className="mission-timeline-detail" aria-label={`Dettaglio step #${selectedSeq}`}>
                <div className="mission-timeline-detail-header">
                  <h3 className="mission-trace-subtitle">
                    Dettaglio step #{selectedSeq}{" "}
                    <span className="muted">
                      {KIND_LABELS[selected.kind] ?? selected.kind}
                    </span>
                  </h3>
                  <button
                    type="button"
                    className="btn secondary mission-timeline-detail-close"
                    aria-label="Chiudi dettaglio step"
                    onClick={() => setSelectedSeq(null)}
                  >
                    ×
                  </button>
                </div>
                <StepDetailMeta step={selected} agentName={agentName} stepModels={stepModels} />
                <StepCard step={selected} missionT0={missionT0} stepModels={stepModels} />
              </section>
            );
          })()}
        </>
      )}

      {steps.length > 0 && (
        <ModelCostTable rows={modelRows} pricingUnmatched={pricingUnmatched} />
      )}

      <IoSection title="Input missione" blocks={missionInput} />
      <IoSection
        title="Output missione"
        blocks={missionOutput}
        emptyLabel={steps.length === 0 ? "In attesa di esecuzione…" : "Nessun output ancora"}
      />
    </div>
  );
}
